<script setup>
/*
    CssEdit
 */
import {onBeforeMount, onMounted, onUnmounted, reactive, ref} from 'vue'
import {clone, waitRender} from '@/paks/vu-app'
import Colors from './Colors'

const props = defineProps({
    modelValue: [Object],
    name: String,
    simple: {type: Boolean, default: false},
    title: String,
})

const page = reactive({
    css: null,
    drag: null,
    fields: [],
    lastKeyPress: null,
    rules: [],
    select: null,
    showColors: {},
    types: null,
})

const Fields = [
    {
        name: 'drag',
        title: 'Order',
        icon: (v, rec) => (rec.path != '/' ? '$drag' : ''),
        cursor: (v, rec) => (rec.path != '/' ? 'grab' : 'default'),
        width: '2%',
    },
    {name: 'delete', icon: '$close', width: '2%'},
    {name: 'expr', title: 'IF', editable: true, width: '10%'},
    {name: 'path', title: 'Element', editable: true, width: '20%'},
    {name: 'property', editable: true, width: '20%'},
    {name: 'value', editable: true, width: '40%'},
    {name: 'swatch', title: ' ', width: '5%', style: (v, rec) => `background-color: ${rec.value}`},
]

const emit = defineEmits(['remove', 'update:modelValue'])

const table = ref(null)

onBeforeMount(() => {
    page.drag = {
        vertical: {dir: 'vertical', move: true, removeOnSpill: false},
    }
    if (props.simple) {
        page.fields = Fields.filter((f) => f.name != 'expr' && f.name != 'path' && f.name != 'swatch')
    } else {
        page.fields = Fields
    }
})

onMounted(() => {
    page.css = props.modelValue
    window.addEventListener('keydown', keyboard, true)
    // load()
})

onUnmounted(() => {
    window.removeEventListener('keydown', keyboard, true)
})

function applyCss(css) {
    for (let item of css) {
        if (!item.property) continue
        // Translate color names to hex values
        item.value = Colors[item.value] || item.value
    }
    page.css = css
    emit('update:modelValue', page.css)
}

function sortCss() {
    let css = page.css.slice(0)
    css.sort((a, b) => a.path.localeCompare(b.name))
    applyCss(css)
}

async function editableEnterKey(event, props) {
    let {field, item, row} = props
    let value = event.target.innerText
    await blur(event, item, field, value, row)
    event.target.blur()
}

async function editableFocus(event, props) {}

async function editableBlur(event, props) {
    let {field, item, row} = props
    let value = event.target.innerText
    await blur(event, item, field, value, row)
}

async function blur(event, item, vfield, value, row = -1) {
    let {css} = page
    if (css.length <= row) {
        return
    }
    delete item._select_
    if (vfield.name == 'property') {
        css[row].property = value
    } else if (vfield.name == 'path') {
        css[row].path = value
    } else if (vfield.name == 'value') {
        css[row].value = value
    }
    if (row == css.length - 1 && vfield.name == 'value') {
        //  Tab pressed in the last cell
        if (page.lastKeyPress == 'Tab') {
            addCss()
        }
        page.lastKeyPress = null
    }
    page.lastKeyPress = null
    applyCss(page.css)
}

async function addCss() {
    let css = page.css.slice(0)
    // 5 fields per row, first editible field is 3rd field (plus 2)
    let id = css.length * 5 + 2
    css.push({property: '', value: ''})
    applyCss(css)
    await waitRender()
    let el = document.getElementById(`${props.name}-${id}`)
    if (el) {
        el.focus()
    }
}

async function clicked({action, column, item, items, row}) {
    if (action == 'add') {
        page.css.push({property: '', value: ''})
    } else if (action == 'delete') {
        for (let item of items) {
            let index = page.css.findIndex((p) => p.path == item.path && p.property == item.property)
            page.css.splice(index, 1)
            applyCss(page.css)
        }
    } else if (column?.name == 'delete') {
        let items = page.css.splice(row, 1)
        if (items) {
            // MOB
            emit('remove', {removed: items[0].path + '.' + items[0].property})
        }
        applyCss(page.css)
    } else if (column?.name == 'swatch') {
        page.showColors[item.path + '.' + item.property] = true
    } else if (action == 'cell') {
    }
}

function formatCss(item, vfield, value) {
    let fieldName = vfield.name
    vfield.attribute = fieldName
    if (fieldName == 'property' || fieldName == 'path' || fieldName == 'value') {
        vfield.editable = true
        vfield.css += ' editable'
    } else {
        vfield.editable = false
    }
    return (value || '').toString()
}

function colorPicked(props, value) {
    page.css[props.row].value = value
    applyCss(page.css)
}

function keyboard(e) {
    page.showColors = {}
    if (e.key == 'Tab' && !e.shiftKey) {
        // Remember if tab pressed without shift key
        page.lastKeyPress = e.key
    }
}

function colorChange() {
    page.showColors = {}
}

function getTabIndex(field) {
    return field.id.match(/\d+$/)[0]
}

async function dragged(event) {
    let {type, index, targetIndex} = event.detail
    if (type == 'drop') {
        let css = clone(page.css)
        css.splice(targetIndex - 0, 0, ...css.splice(index - 0, 1))
        applyCss(css)
    }
}

function isColor(rec) {
    if (!rec || !rec.property || !rec.value) return false
    let {property, value} = rec
    if (property == 'background' || property?.indexOf('color') >= 0) {
        if (value.indexOf(':') < 0 && value.indexOf('gradient') < 0) {
            return true
        }
    }
    return false
}
</script>

<template>
    <vu-table
        class="css-edit"
        options="dynamic,toolbar"
        ref="table"
        row-key="name"
        width="100%"
        nodata="No Items"
        :callbacks="{format: formatCss}"
        :drag="page.drag"
        :data="page.css"
        :fields="page.fields"
        :name="props.name"
        :reload="false"
        :title="title"
        @click="clicked"
        @drag="dragged">
        <template v-slot:more="props">
            <v-btn size="small" color="primary" class="mr-2 add" @click="addCss">Add</v-btn>
            <v-btn size="small" color="primary" class="mr-2 add" @click="sortCss">Sort</v-btn>
        </template>
        <template v-slot:cell="props">
            <div
                v-if="props.field.editable"
                spellcheck="false"
                class="cell-value"
                :contenteditable="props.field.editable"
                :id="props.field.id"
                :tabindex="getTabIndex(props.field)"
                @blur="editableBlur($event, props)"
                @focus="editableFocus($event, props)"
                @keydown.enter.prevent="editableEnterKey($event, props)">
                {{ props.field.value || ' ' }}

            </div>
            <div v-else class="cell-value" contenteditable="false">{{ props.field.value }}</div>
            <v-dialog
                v-if="props.field.name == 'swatch' && isColor(props.item)"
                v-model="page.showColors[props.item.path + '.' + props.item.property]"
                content-class="css-color-picker"
                @afterLeave="colorChange">
                <v-color-picker
                    v-model="props.item.value"
                    @update:modelValue="colorPicked(props, $event)"
                    :hide-sliders="false"></v-color-picker>
            </v-dialog>
        </template>
    </vu-table>
</template>

<style lang="scss" scoped>
.css-edit {
    width: 100%;
    h2 {
        font-size: 1.4rem;
        font-weight: normal;
    }
    .v-alert {
        width: 100%;
    }
    label {
        color: rgb(var(--v-theme-text));
        font-weight: normal;
        font-size: 1.2rem;
    }

    td.editable {
        padding: 4px 7px 4px 7px;
        background: rgba(130, 178, 255, 0.2) !important;
        margin: 2px;
        cursor: text;
        &:focus {
            outline: 2px solid rgba(0, 0, 255, 0.75) !important;
            background: white !important;
            width: calc(100% - 4px);
        }
        .__swatch {
            display: inline-block;
            border: solid 1px rgba(var(--v-theme-primary), 0.5);
            width: 16px;
            height: 16px;
            margin-left: 4px;
            vertical-align: middle;
            cursor: pointer;
        }
        .cell-value:focus,
        .cell-value:focus-visible {
            outline: 2px solid rgba(0, 0, 255, 0.75) !important;
        }
    }
    .table-cell {
        padding: 4px !important;
        .cell-attribute {
            padding: 3px;
            background: rgba(50, 50, 50, 0.1);
            color: black;
        }
    }
    .table-cell.table-col-name {
        .cell-value {
            display: inline-block;
            width: 100%;
            margin: 1px;
            padding: 3px;
        }
    }
    .table-cell.table-col-value {
        .cell-value {
            display: inline-block;
            width: calc(100% - 20px);
            margin: 1px;
            padding: 3px;
            padding-right: 0;
        }
    }
    .color-icon {
        margin-top: 2px;
        float: right;
        width: 16px;
    }
    .table-title {
        font-size: 1rem !important;
    }
    .add {
        font-size: 12px;
    }
}
</style>

<style lang="scss">
.css-color-picker {
    width: fit-content !important;
}
</style>
